import { Theme } from 'ia-react-core/~/interfaces/Theme';
import { ReactComponent as ReportIcon } from '../../icons/report.svg';
import baseIaTheme from '../baseIaTheme';
import FadingLoader from '../../components/loader/FadingLoader';
import baseTheme from '../baseTheme';

const colors = {
  ...baseIaTheme.colors,
  lightest: baseIaTheme.colors.blue3,
  lighter: baseIaTheme.colors.blue10,
  light: baseIaTheme.colors.blue5,
  primary: baseIaTheme.colors.blue6,
  dark: baseIaTheme.colors.blue7,
  darker: baseIaTheme.colors.blue8,
  darkest: baseIaTheme.colors.blue9,
  disabled: baseIaTheme.colors.gray4,
  success: baseIaTheme.colors.green10,
  successBackground: baseIaTheme.colors.green11,
  warning: baseIaTheme.colors.orange2,
  warningBackground: baseIaTheme.colors.orange3,
  caution: baseIaTheme.colors.yellow4,
  cautionBackground: baseIaTheme.colors.yellow5,
  error: baseIaTheme.colors.red14,
  errorBackground: baseIaTheme.colors.red15,
  info: baseIaTheme.colors.blue5,
  infoBackground: baseIaTheme.colors.blue12,
  text: baseIaTheme.colors.black,
  link: baseIaTheme.colors.blue5,
  linkHover: baseIaTheme.colors.blue4,
  focus: baseIaTheme.colors.blue2,
  buttonBorder: baseIaTheme.colors.blue6,
  linkWidget: baseIaTheme.colors.blue5,
};

export type DesignSystemTheme = Theme;

const designSystemTheme: Theme = (() => ({
  ...baseIaTheme,

  colors,

  boxShadows: {
    ...baseIaTheme.boxShadows,
    focus: `0 0 3px 1px ${colors.blue10}`,
  },

  icons: {
    ...baseIaTheme.icons,
    error: ReportIcon,
  },

  assets: {
    ...baseIaTheme.assets,
    mainLoader: FadingLoader,
  },

  formatters: {
    ...baseIaTheme.formatters,
    emptyData: '-',
  },

  layout: {
    ...baseIaTheme.layout,
    headerHeight: 82,
    horizontalLogoHeaderHeight: 58,
    headerPadding: '8px 16px',
    lgHeaderPadding: '16px 32px',
    footerPadding: '48px 16px',
    lgFooterPadding: '48px 32px',
    contentVerticalPadding: 60,
    lgContentVerticalPadding: 96,
    contentHorizontalPadding: 16,
    lgContentHorizontalPadding: 32,
    maxHeaderContentWidth: 1000000,
    maxFooterContentWidth: 1000000,
    noAppHeader: true,
    contentBackground: colors.gray1,
    footerBackground: colors.white,
    noHeaderLanguageButton: true,
    minAppHeaderHeight: 2,
    lgMinAppHeaderHeight: 2,
    appHeaderPadding: 0,
    noTransactionProgressName: true,
    noTransactionProgressPercent: true,
    transactionProgressMarginBottom: 0,
    transactionProgressBarBackground: colors.gray15,
    transactionProgressBarProgressBackground: colors.blue3,
    noFooterLanguageButton: false,
  },

  MainLogo: {
    ...baseTheme.MainLogo,
    width: '77px',
    height: '42px',
    lgWidth: '92px',
    lgHeight: '50px',
  },

  Button: {
    ...baseIaTheme.Button,
    borderRadius: '25px',
    disabledFontStyle: 'normal',
  },
  IconButton: {
    color: colors.light,
    hoverColor: colors.lighter,
    activeColor: colors.lightest,
  },
  PrimaryButton: {
    ...baseIaTheme.PrimaryButton,
    reversedLoader: true,
    fontWeight: 'bold',
  },
  SecondaryButton: {
    ...baseIaTheme.SecondaryButton,
    hoverBorderColor: colors.light,
    hoverColor: colors.light,
    hoverBorderWidth: 3,
    activeBorderColor: colors.lighter,
    activeColor: colors.lighter,
    disabledBackground: colors.white,
    disabledColor: colors.disabled,
    disabledBorderColor: colors.disabled,
    activeBorderWidth: 3,
  },
  TertiaryButton: {
    ...baseIaTheme.TertiaryButton,
    borderRadius: '4px',
    reversedLoader: true,
    hoverColor: colors.light,
    hoverBorderColor: colors.light,
    disabledBackground: colors.white,
    disabledColor: colors.disabled,
    disabledBorderColor: colors.disabled,
  },
  ToggleButton: {
    ...baseIaTheme.ToggleButton,
    borderRadius: '4px',
  },
  ActionBar: {
    ...baseIaTheme.ActionBar,
    justifyContent: 'flex-end',
    buttonMargin: '8px',
    buttonFlexGrow: 0,
    minButtonWidth: 0,
    margin: '-8px',
  },
  ConfirmBar: {
    ...baseIaTheme.ConfirmBar,
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
    flexButtons: false,
  },
  Modal: {
    ...baseIaTheme.Modal,
    headerHeight: 40,
    headerBackground: 'transparent',
    background: colors.white,
    closeButtonBackground: 'transparent',
    closeButtonHoverBackground: 'transparent',
    closeButtonActiveBackground: 'transparent',
    closeButtonMargin: '0 -8px 0 0 ',
    closeButtonRight: -8,
    verticalPadding: 48,
    horizontalPadding: 32,
    overlayBackground: 'rgba(0, 0, 0, .8)',
    footerTopPadding: '36px',
    horizontalMarginFromEdgeXs: 16,
    bottomMarginFromEdgeXs: 16,
    mobileFlexHeight: true,
    mobileFullScroll: true,
    noHeaderIfClosable: true,
    closableErrorModal: false,
  },
  ErrorModal: {
    iconSize: 48,
    iconOffset: -5,
    iconColWrap: 'wrap',
    iconColMinWidth: '100%',
    iconColMarginBottom: 24,
    gutterWidth: 0,
  },
  Input: {
    ...baseIaTheme.Input,
    hoverBorderColor: colors.light,
    disabledFontStyle: 'normal',
    disabledColor: colors.shadowGray3,
    borderRadius: '4px',
    borderColor: colors.gray13,
    bottomBorderColor: colors.gray13,
    validBottomBorderColor: '',
    focusBorder: `2px solid ${colors.blue10}`,
    focusPadding: '9px 9px 10px 8px',
    invalidPadding: '9px 9px 10px 8px',
    invalidBorder: `2px solid ${colors.red14}`,
    invalidHoverBorderColor: colors.red14,
    warningPadding: '9px 9px 10px 8px',
    warningBorder: `2px solid ${colors.warning}`,
    warningHoverBorderColor: colors.warning,
    cautionPadding: '9px 9px 10px 8px',
    cautionBorder: `2px solid ${colors.caution}`,
    cautionHoverBorderColor: colors.caution,
    signFocusOffset: -1,
  },
  Dropdown: {
    ...baseIaTheme.Dropdown,
    focusPadding: '9px 33px 10px 8px',
    invalidPadding: '9px 33px 10px 8px',
  },
  EditableDropdown: {
    ...baseIaTheme.Dropdown,
    focusPadding: '9px 40px 10px 8px',
    invalidPadding: '9px 40px 10px 8px',
  },
  FormErrorMessage: {
    ...baseIaTheme.FormErrorMessage,
    padding: '10px 0 0 0',
    iconSize: 18,
    fontWeight: 'bold',
    errorColor: colors.red14,
    warningColor: colors.warning,
    cautionColor: colors.caution,
    iconPaddingRight: 2,
    iconTop: -3,
  },
  Form: {
    formColGutter: 8,
  },
  InlineMessage: {
    ...baseIaTheme.InlineMessage,
    borderRadius: '4px',
    iconTop: '1px',
  },
  Message: {
    ...baseIaTheme.Message,
    fontSize: '14px',
    iconTop: '21px',
    padding: '24px 16px',
    borderRadius: '4px',
    minHeight: `${48 + 38}px`,
    titleMargin: '0 0 4px 0',
    display: 'flex',
    alignItems: 'center',
  },
  RadioItem: {
    ...baseIaTheme.RadioItem,
    labelTextPaddingLeft: 12,
    hoverColor: colors.light,
  },
  RadioButton: {
    ...baseIaTheme.RadioButton,
    hoverOutlineColor: colors.light,
    disabledBackground: colors.white,
  },
  RadioList: {
    ...baseIaTheme.RadioList,
    padding: '0',
    itemPadding: '10px 12px',
    horizontalItemPadding: '10px 0 10px 12px',
    noBoxItemPadding: '0 0 16px 0',
    noBoxLastItemMarginBottom: '-16px',
    boxBorderColor: colors.gray4,
    boxBorderBottomColor: colors.gray4,
    shadedBoxBorderWidth: '1px',
    shadedBoxBorderColor: colors.gray4,
    shadedBoxBorderBottomColor: colors.gray4,
    boxItemHoverBackground: colors.blue12,
    itemMarginBottom: 0,
    invalidBoxBorderColor: colors.error,
    warningBoxBorderColor: colors.warning,
    cautionBoxBorderColor: colors.caution,
    invalidBoxBorderWidth: '2px',
    invalidBoxOffset: '-1px',
    invalidBoxOffsetPadding: '10px 11px 10px 11px',
    invalidBoxOffsetVerticalPadding: '9px',
  },
  CheckboxItem: {
    ...baseIaTheme.CheckboxItem,
    hoverColor: colors.light,
    labelTextPaddingLeft: 12,
  },
  Checkbox: {
    ...baseIaTheme.Checkbox,
    hoverOutlineColor: colors.light,
    disabledBackground: colors.white,
  },
  CheckboxList: {
    ...baseIaTheme.CheckboxList,
    noBox: false,
    padding: '0',
    itemPadding: '10px 12px',
    horizontalItemPadding: '10px 0 10px 12px',
    noBoxItemPadding: '0 0 16px 0',
    noBoxLastItemMarginBottom: '-16px',
    boxBorderColor: colors.gray4,
    boxBorderBottomColor: colors.gray4,
    shadedBoxBorderWidth: '1px',
    shadedBoxBorderColor: colors.gray4,
    shadedBoxBorderBottomColor: colors.gray4,
    boxItemHoverBackground: colors.blue12,
    itemMarginBottom: 0,
    invalidBoxBorderColor: colors.error,
    warningBoxBorderColor: colors.warning,
    cautionBoxBorderColor: colors.caution,
    invalidBoxBorderWidth: '2px',
    invalidBoxOffset: '-1px',
    invalidBoxOffsetPadding: '10px 11px 10px 11px',
    invalidBoxOffsetVerticalPadding: '9px',
  },
  Box: {
    ...baseIaTheme.Box,
    borderRadius: '4px',
  },
  Block: {
    ...baseIaTheme.Block,
    borderRadius: '4px',
    padding: '36px 16px',
    mdPadding: '36px 32px',
    boxShadow: baseIaTheme.boxShadows.medium,
  },
  Collapse: {
    boxShadow: baseIaTheme.boxShadows.medium,
  },
}))();

/**
 * @since 0.26.0
 * @visibleName designSystemTheme
 * @component
 */
export default designSystemTheme;
